import { useEffect, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  MoreHorizontal,
  Plus,
  Settings,
  Trash2,
  Upload,
} from 'lucide-react';
import type { Account, NetworkType } from '@mls-wallet/shared-types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TransactionBuilderHeader } from '@/components/transaction-builder/TransactionBuilderHeader';
import { TransactionBuilderFooter } from '@/components/transaction-builder/TransactionBuilderFooter';
import {
  TransactionBuilderPageType,
  useTransactionBuilderStore,
} from '@/components/transaction-builder/useTransactionBuilderStore';
import { BatchTransactionSummary } from '@/components/transaction-builder/BatchTransactionSummary';
import {
  buildBatchTransactionChild,
  getBatchTransactionTypeLabel,
  type BatchTransactionInput,
} from '@/components/transaction-builder/batch-transaction-draft';
import { RawBatchTransactionEditor } from '@/components/transaction-builder/RawBatchTransactionEditor';
import { getTransactionPolicyError } from '@/components/transaction-builder/transaction-policy-validation';
import { useBatchSimulation } from '@/components/transaction-builder/useBatchSimulation';
import { TransactionSimulationSheet } from '@/components/transaction-builder/transaction-simulation/TransactionSimulationSheet';
import { useTransactionBuilderPolicies } from '@/hooks/policy/use-transaction-builder-policies';
import { useAuthClient } from '@/lib/authClient';
import { getPolicyApprovalDisplay } from '@/utils/policyFormatting';
import { cn } from '@/lib/utils';
import { TransactionBuilderPolicySelectionPage } from './TransactionBuilderPolicySelectionPage';
import { TransactionBuilderTransactionDetailsPage } from './TransactionBuilderTransactionDetailsPage';

/** One cart owns both UI and JSON children; dialogs edit an isolated scratch form. */
export function TransactionBuilderCartPage({
  account,
  network,
}: {
  account: Account;
  network: NetworkType;
}) {
  const children = useTransactionBuilderStore(
    (state) => state.batchTransactions
  );
  const page = useTransactionBuilderStore((state) => state.currentPage.page);
  const sessionId = useTransactionBuilderStore(
    (state) => state.editorSessionId
  );
  const editingId = useTransactionBuilderStore(
    (state) => state.editingTransactionId
  );
  const raw = useTransactionBuilderStore(
    (state) => state.editingRawTransaction
  );
  const transactionType = useTransactionBuilderStore(
    (state) => state.transactionType
  );
  const navigate = useTransactionBuilderStore(
    (state) => state.navigateForwardToPage
  );
  const begin = useTransactionBuilderStore(
    (state) => state.beginBatchTransaction
  );
  const edit = useTransactionBuilderStore(
    (state) => state.editBatchTransaction
  );
  const cancel = useTransactionBuilderStore(
    (state) => state.cancelBatchTransaction
  );
  const remove = useTransactionBuilderStore(
    (state) => state.removeBatchTransaction
  );
  const move = useTransactionBuilderStore(
    (state) => state.moveBatchTransaction
  );
  const save = useTransactionBuilderStore(
    (state) => state.saveBatchTransaction
  );
  const {
    policies,
    isLoading,
    error: policiesError,
  } = useTransactionBuilderPolicies({
    accountId: account.id,
    networkId: network,
  });
  const { user } = useAuthClient();
  const [emptyWarning, setEmptyWarning] = useState(false);
  const [changeNetworkWarning, setChangeNetworkWarning] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [cartError, setCartError] = useState<string | null>(null);
  const [simulationOpen, setSimulationOpen] = useState(false);
  const returnFocus = useRef<HTMLElement | null>(null);
  const { request, revision } = useBatchSimulation();
  const editorOpen =
    page === TransactionBuilderPageType.POLICY_SELECTION_PAGE ||
    page === TransactionBuilderPageType.TRANSACTION_DETAILS_PAGE;

  useEffect(() => {
    setSimulationOpen(false);
    setCartError(null);
  }, [revision]);
  useEffect(() => {
    setEditorError(null);
  }, [sessionId, page]);

  const rememberFocus = () => {
    returnFocus.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
  };
  const add = () => {
    rememberFocus();
    begin();
  };
  const policyError = (child: BatchTransactionInput): string | null => {
    if (!user || isLoading) return 'Wait for policies to finish loading.';
    if (policiesError)
      return 'Policies could not be loaded. Try again before continuing.';
    const policy = policies?.find(({ id }) => id === child.payload.policyId);
    if (!policy)
      return 'This policy is no longer available. Choose another policy.';
    return getTransactionPolicyError(child.payload, policy, {
      multichainAccountId: account.id,
      networkId: network,
      userId: user.id,
    });
  };
  /**
   * Revalidate the child against current policies and save the matching snapshot.
   * Throws the policy message if it is no longer eligible; callers must catch
   * the error and display its message.
   */
  const saveChild = (child: BatchTransactionInput) => {
    const error = policyError(child);
    if (error) throw new Error(error);
    const currentPolicy = policies?.find(
      ({ id }) => id === child.payload.policyId
    );
    if (!currentPolicy) return;
    save({ ...child, policy: currentPolicy });
  };
  const saveForm = () => {
    const state = useTransactionBuilderStore.getState();
    try {
      // Case: a cleared policy leaves the open editor unable to authorize its child.
      if (!state.policy) throw new Error('Choose a policy before saving.');
      if (
        state.transactionType === 'token-transfer' &&
        state.tokenTransferDetails
      ) {
        saveChild(
          buildBatchTransactionChild(state.policy, {
            transactionType: 'token-transfer',
            tokenTransferDetails: state.tokenTransferDetails,
          })
        );
      } else if (
        state.transactionType === 'contract-interaction' &&
        state.contractInteractionDetails
      ) {
        saveChild(
          buildBatchTransactionChild(state.policy, {
            transactionType: 'contract-interaction',
            contractInteractionDetails: state.contractInteractionDetails,
          })
        );
      } else {
        // Case: incomplete editor details cannot produce a child to save.
        throw new Error('Complete the transaction details before saving.');
      }
    } catch (cause) {
      setEditorError(
        cause instanceof Error
          ? cause.message
          : 'Check the transaction details.'
      );
    }
  };
  const continueToDescription = () => {
    if (children.length === 0) {
      setEmptyWarning(true);
      return;
    }
    const index = children.findIndex((child) => policyError(child));
    const invalid = children[index];
    if (invalid) {
      setCartError(`Transaction ${index + 1}: ${policyError(invalid)}`);
      return;
    }
    // Carry the currently displayed policy requirements into review.
    useTransactionBuilderStore.setState({
      batchTransactions: children.map((child) => ({
        ...child,
        policy:
          policies?.find(({ id }) => id === child.policy.id) ?? child.policy,
      })),
    });
    navigate({ page: TransactionBuilderPageType.DESCRIPTION_PAGE });
  };
  const title =
    page === TransactionBuilderPageType.POLICY_SELECTION_PAGE
      ? 'Select a policy'
      : raw
        ? 'Edit imported transaction'
        : `${editingId ? 'Edit' : 'Add'} ${transactionType === 'token-transfer' ? 'token transfer' : 'contract interaction'}`;

  return (
    <>
      <TransactionBuilderHeader
        title="What transactions do you want to queue?"
        showBackButton
        onBack={() =>
          children.length
            ? setChangeNetworkWarning(true)
            : navigate({
                page: TransactionBuilderPageType.NETWORK_SELECTION_PAGE,
              })
        }
      />
      <div
        className={cn(
          'mx-auto flex w-full max-w-[932px] flex-col gap-4 px-4 pb-8',
          children.length ? 'pt-[34px]' : 'pt-[54px]'
        )}
      >
        <div className="flex min-h-8 flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-medium">
            Transactions ({children.length})
          </h2>
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 text-muted-foreground"
            onClick={() => {
              useTransactionBuilderStore.getState().clearImportedTransactions();
              navigate({ page: TransactionBuilderPageType.JSON_IMPORT_PAGE });
            }}
          >
            <Upload className="size-4" />
            Import JSON
          </Button>
        </div>
        {children.length === 0 ? (
          <button
            type="button"
            onClick={add}
            className="group flex min-h-[300px] w-full flex-col items-center justify-center gap-3 rounded-[24px] border border-zinc-300 bg-neutral-50 p-6 text-center shadow-[0_6px_20px_rgba(24,24,27,0.05)] transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-zinc-700 dark:bg-zinc-900"
          >
            <span className="flex size-12 items-center justify-center rounded-full bg-muted group-hover:bg-violet-100">
              <Plus className="size-[18px]" />
            </span>
            <span className="text-base font-semibold">
              Add your first transaction
            </span>
            <span className="text-sm text-muted-foreground">
              No transactions in this batch yet
            </span>
            <span className="rounded-[10px] border border-zinc-300 bg-muted px-4 py-[9px] text-sm font-medium dark:border-zinc-700">
              Add transaction
            </span>
          </button>
        ) : (
          <>
            <ol
              className="space-y-4"
              aria-label="Transactions in execution order"
            >
              {children.map((child, index) => (
                <li key={child.id} className="group relative">
                  <button
                    type="button"
                    aria-label={`Edit transaction ${index + 1}`}
                    onClick={() => {
                      rememberFocus();
                      edit(child.id);
                    }}
                    className="flex min-h-[129px] w-full flex-col gap-[13px] rounded-2xl border border-white bg-neutral-50 px-6 py-[27px] text-left transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:px-[39px] dark:border-zinc-800 dark:bg-zinc-900"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span className="text-muted-foreground">
                        Policy · {child.policy.name}
                      </span>
                      <span className="text-zinc-400">
                        {getBatchTransactionTypeLabel(child)}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1 text-base">
                        <BatchTransactionSummary
                          child={child}
                          network={network}
                        />
                      </div>
                      <span className="shrink-0 text-sm text-muted-foreground">
                        {getPolicyApprovalDisplay(child.policy)}
                      </span>
                    </div>
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        aria-label={`Actions for transaction ${index + 1}`}
                        className="absolute -right-2 -top-2 size-8 rounded-full bg-background opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
                      >
                        <MoreHorizontal className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        disabled={index === 0}
                        onSelect={() => move(child.id, -1)}
                      >
                        <ArrowUp className="mr-2 size-4" />
                        Move up
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={index === children.length - 1}
                        onSelect={() => move(child.id, 1)}
                      >
                        <ArrowDown className="mr-2 size-4" />
                        Move down
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onSelect={() => remove(child.id)}
                      >
                        <Trash2 className="mr-2 size-4" />
                        Remove transaction
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </li>
              ))}
            </ol>
            <Button
              variant="outline"
              onClick={add}
              className="h-[52px] w-full gap-2 rounded-2xl border-zinc-300 bg-background text-sm font-semibold"
            >
              <Plus className="size-[15px]" />
              Add another transaction
            </Button>
          </>
        )}
        {cartError && (
          <p role="alert" className="text-sm text-destructive">
            {cartError}
          </p>
        )}
      </div>
      <TransactionBuilderFooter
        onContinue={continueToDescription}
        secondaryAction={
          children.length
            ? {
                label: 'Simulate transactions',
                icon: <Settings className="size-[15px]" />,
                disabled: !request || isLoading || !!policiesError,
                onClick: () => setSimulationOpen(true),
              }
            : undefined
        }
      />
      {request && (
        <TransactionSimulationSheet
          key={revision}
          open={simulationOpen && !editorOpen}
          onOpenChange={setSimulationOpen}
          source="draft"
          request={request}
        />
      )}
      <Dialog
        open={editorOpen}
        onOpenChange={(open) => {
          if (!open) cancel();
        }}
      >
        <DialogContent
          aria-describedby={undefined}
          dialogOverlayProps={{ className: 'bg-zinc-900/30' }}
          className="font-[Inter,sans-serif] left-0 top-[56px] flex h-[calc(100dvh-56px)] max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-t-[32px] border-0 p-0 sm:rounded-b-none sm:rounded-t-[50px] [&>button]:right-[30px] [&>button]:top-[21px]"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            returnFocus.current?.focus();
          }}
        >
          <DialogTitle className="flex h-[65px] shrink-0 items-center justify-center border-b px-16 text-center text-[20px] font-medium tracking-normal">
            {title}
          </DialogTitle>
          <div className="min-h-0 flex-1 overflow-y-auto bg-muted">
            {editorError && (
              <p
                role="alert"
                className="mx-auto max-w-[738px] px-[30px] pt-4 text-sm text-destructive"
              >
                {editorError}
              </p>
            )}
            {/* Unmount the scratch form immediately. Radix keeps the dialog
                mounted during its exit animation after the session is cleared. */}
            {editorOpen &&
              (page === TransactionBuilderPageType.POLICY_SELECTION_PAGE ? (
                <TransactionBuilderPolicySelectionPage
                  account={account}
                  network={network}
                  embedded
                  onCancel={cancel}
                />
              ) : raw ? (
                <RawBatchTransactionEditor
                  key={sessionId}
                  initialValue={raw}
                  onSave={(payload) => {
                    const policy = useTransactionBuilderStore.getState().policy;
                    if (policy)
                      saveChild({
                        payload: { ...payload, policyId: policy.id },
                        policy,
                      });
                  }}
                />
              ) : (
                <TransactionBuilderTransactionDetailsPage
                  key={sessionId}
                  embedded
                  isEditing={!!editingId}
                  onSave={saveForm}
                />
              ))}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={emptyWarning} onOpenChange={setEmptyWarning}>
        <DialogContent
          className="font-[Inter,sans-serif] gap-[18px] rounded-[24px] px-8 pt-8 pb-7 sm:max-w-[560px] sm:rounded-[24px] [&>button]:hidden"
          dialogOverlayProps={{ className: 'bg-zinc-900/35' }}
        >
          <DialogHeader className="space-y-[18px] text-left">
            <DialogTitle className="text-[20px] leading-normal tracking-normal">
              Your batch is empty
            </DialogTitle>
            <DialogDescription className="text-[15px] leading-[22px]">
              Add at least one transaction before continuing to the batch
              description.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-3 sm:space-x-0">
            <Button
              className="h-[39px] rounded-[10px] px-[18px] sm:w-[92px]"
              variant="outline"
              onClick={() => setEmptyWarning(false)}
            >
              Cancel
            </Button>
            <Button
              className="h-[39px] rounded-[10px] px-[18px]"
              onClick={() => {
                setEmptyWarning(false);
                add();
              }}
            >
              Add a transaction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={changeNetworkWarning}
        onOpenChange={setChangeNetworkWarning}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change the batch network?</DialogTitle>
            <DialogDescription>
              Choosing a network clears the transactions in this batch.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setChangeNetworkWarning(false)}
            >
              Keep editing
            </Button>
            <Button
              onClick={() =>
                navigate({
                  page: TransactionBuilderPageType.NETWORK_SELECTION_PAGE,
                })
              }
            >
              Change network
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
